import React, { useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import { COMPARE_LANGUAGES, COMPARE_IDEAS } from '../data/grammarCompareData';
import { COMPARE_TENSES } from '../data/grammarCompareTenses';
import './GrammarCompare.css';

const ALL_LANGUAGES = ['english', 'italian', 'spanish', 'french', 'portuguese'];
const UI_TO_LANG = { en: 'english', it: 'italian', es: 'spanish', fr: 'french', pt: 'portuguese' };
const FLAGS = { english: '🇬🇧', italian: '🇮🇹', spanish: '🇪🇸', french: '🇫🇷', portuguese: '🇧🇷' };

const LANGUAGE_NAMES = {
    en: { english: 'English', italian: 'Italian', spanish: 'Spanish', french: 'French', portuguese: 'Portuguese' },
    it: { english: 'Inglese', italian: 'Italiano', spanish: 'Spagnolo', french: 'Francese', portuguese: 'Portoghese' },
    es: { english: 'Inglés', italian: 'Italiano', spanish: 'Español', french: 'Francés', portuguese: 'Portugués' },
    fr: { english: 'Anglais', italian: 'Italien', spanish: 'Espagnol', french: 'Français', portuguese: 'Portugais' },
    pt: { english: 'Inglês', italian: 'Italiano', spanish: 'Espanhol', french: 'Francês', portuguese: 'Português' },
};

const TEXT = {
    en: {
        intro: 'See how the same idea is said in two languages, side by side.',
        mine: 'My language',
        learning: 'I am learning',
        swap: 'Swap languages',
        samePick: 'Pick two different languages to compare.',
        soonTitle: 'This pair is coming soon',
        soonBody: 'For now, English, Italian, French, Spanish and Portuguese can be compared. More languages are on the way.',
        tryPair: 'Compare English and Italian',
        forms: 'How it is formed',
        examples: 'Examples',
        watchOut: 'Watch out',
        all: 'All',
        byTopic: 'By topic',
        byTense: 'By tense',
    },
    it: {
        intro: 'Guarda come la stessa idea si esprime in due lingue, una accanto all\'altra.',
        mine: 'La mia lingua',
        learning: 'Sto imparando',
        swap: 'Inverti le lingue',
        samePick: 'Scegli due lingue diverse da confrontare.',
        soonTitle: 'Questa coppia arriva presto',
        soonBody: 'Per ora puoi confrontare inglese, italiano, francese, spagnolo e portoghese. Altre lingue arriveranno presto.',
        tryPair: 'Confronta inglese e italiano',
        forms: 'Come si forma',
        examples: 'Esempi',
        watchOut: 'Attenzione',
        all: 'Tutti',
        byTopic: 'Per argomento',
        byTense: 'Per tempo verbale',
    },
    es: {
        intro: 'Mira cómo se dice la misma idea en dos idiomas, lado a lado.',
        mine: 'Mi idioma',
        learning: 'Estoy aprendiendo',
        swap: 'Intercambiar idiomas',
        samePick: 'Elige dos idiomas distintos para comparar.',
        soonTitle: 'Este par llega pronto',
        soonBody: 'Por ahora puedes comparar inglés, italiano, francés, español y portugués. Pronto llegarán más idiomas.',
        tryPair: 'Comparar inglés e italiano',
        forms: 'Cómo se forma',
        examples: 'Ejemplos',
        watchOut: 'Atención',
        all: 'Todos',
        byTopic: 'Por tema',
        byTense: 'Por tiempo verbal',
    },
    fr: {
        intro: 'Vois comment la même idée s\'exprime dans deux langues, côte à côte.',
        mine: 'Ma langue',
        learning: 'J\'apprends',
        swap: 'Inverser les langues',
        samePick: 'Choisis deux langues différentes à comparer.',
        soonTitle: 'Cette paire arrive bientôt',
        soonBody: 'Pour l\'instant, tu peux comparer l\'anglais, l\'italien, le français, l\'espagnol et le portugais. D\'autres langues arrivent bientôt.',
        tryPair: 'Comparer l\'anglais et l\'italien',
        forms: 'Comment ça se forme',
        examples: 'Exemples',
        watchOut: 'Attention',
        all: 'Tous',
        byTopic: 'Par sujet',
        byTense: 'Par temps',
    },
    pt: {
        intro: 'Veja como a mesma ideia é dita em dois idiomas, lado a lado.',
        mine: 'Meu idioma',
        learning: 'Estou aprendendo',
        swap: 'Trocar idiomas',
        samePick: 'Escolha dois idiomas diferentes para comparar.',
        soonTitle: 'Este par chega em breve',
        soonBody: 'Por enquanto, você pode comparar inglês, italiano, francês, espanhol e português. Mais idiomas estão a caminho.',
        tryPair: 'Comparar inglês e italiano',
        forms: 'Como se forma',
        examples: 'Exemplos',
        watchOut: 'Atenção',
        all: 'Todos',
        byTopic: 'Por tema',
        byTense: 'Por tempo verbal',
    },
};

// An idea can be shown for a pair only when both languages have forms for it
const hasLanguage = (idea, lang) => (idea.forms[lang] || []).length > 0;
const pairKeyOf = (a, b) => [a, b].sort().join('-');

export default function GrammarCompare() {
    const { interfaceLanguage } = useLanguage();
    const { activeLanguage } = useUserLanguage();
    const uiLang = TEXT[interfaceLanguage] ? interfaceLanguage : 'en';
    const tx = (key) => TEXT[uiLang][key];
    const names = LANGUAGE_NAMES[uiLang];
    // Explanations exist in all five languages; any missing text falls back to English
    const pick = (obj) => (obj && (obj[uiLang] || obj.en)) || '';
    // Form names and example tags are plain text, or text in several languages
    const labelOf = (value) => (typeof value === 'string' ? value : pick(value));

    const [from, setFrom] = useState(UI_TO_LANG[interfaceLanguage] || 'english');
    const [to, setTo] = useState((activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase());
    const [openId, setOpenId] = useState(COMPARE_IDEAS.length > 0 ? COMPARE_IDEAS[0].id : null);
    const [levelFilter, setLevelFilter] = useState('all');
    const [view, setView] = useState('topic');
    const [tenseFilter, setTenseFilter] = useState(COMPARE_TENSES[0].id);

    const same = from === to;
    const ready = !same && COMPARE_LANGUAGES.includes(from) && COMPARE_LANGUAGES.includes(to);

    const pairKey = pairKeyOf(from, to);
    const pairIdeas = ready ? COMPARE_IDEAS.filter(idea => hasLanguage(idea, from) && hasLanguage(idea, to)) : [];
    const levels = [...new Set(pairIdeas.map(idea => idea.level))];
    const activeFilter = levels.includes(levelFilter) ? levelFilter : 'all';
    const activeTense = COMPARE_TENSES.find(t => t.id === tenseFilter) || COMPARE_TENSES[0];
    const visibleIdeas = view === 'tense'
        ? pairIdeas.filter(idea => activeTense.ideas.includes(idea.id))
        : (activeFilter === 'all' ? pairIdeas : pairIdeas.filter(idea => idea.level === activeFilter));

    const swap = () => {
        setFrom(to);
        setTo(from);
    };

    const showEnglishItalian = () => {
        setFrom('english');
        setTo('italian');
    };

    return (
        <div className="gc-wrap">
            <div className="gc-intro">{tx('intro')}</div>

            <div className="gc-pick">
                <label className="gc-pick-col">
                    <span className="gc-pick-label">{tx('mine')}</span>
                    <select className="gc-select" value={from} onChange={e => setFrom(e.target.value)}>
                        {ALL_LANGUAGES.map(lang => (
                            <option key={lang} value={lang}>{FLAGS[lang]} {names[lang]}</option>
                        ))}
                    </select>
                </label>
                <button className="gc-swap" onClick={swap} aria-label={tx('swap')}>⇄</button>
                <label className="gc-pick-col">
                    <span className="gc-pick-label">{tx('learning')}</span>
                    <select className="gc-select" value={to} onChange={e => setTo(e.target.value)}>
                        {ALL_LANGUAGES.map(lang => (
                            <option key={lang} value={lang}>{FLAGS[lang]} {names[lang]}</option>
                        ))}
                    </select>
                </label>
            </div>

            {same && <div className="gc-note">{tx('samePick')}</div>}

            {!same && !ready && (
                <div className="gc-soon">
                    <div className="gc-soon-title">{tx('soonTitle')}</div>
                    <div className="gc-soon-body">{tx('soonBody')}</div>
                    <button className="gc-soon-btn" onClick={showEnglishItalian}>{tx('tryPair')}</button>
                </div>
            )}

            {ready && (
                <div className="gc-levels">
                    {[['topic', tx('byTopic')], ['tense', tx('byTense')]].map(([key, label]) => (
                        <button
                            key={key}
                            className={`gc-level-chip ${view === key ? 'active' : ''}`}
                            onClick={() => setView(key)}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            )}

            {ready && view === 'tense' && (
                <div className="gc-levels">
                    {COMPARE_TENSES.map(t => (
                        <button
                            key={t.id}
                            className={`gc-level-chip ${activeTense.id === t.id ? 'active' : ''}`}
                            onClick={() => setTenseFilter(t.id)}
                        >
                            {t.label[uiLang] || t.label.en}
                        </button>
                    ))}
                </div>
            )}

            {ready && view === 'topic' && (
                <div className="gc-levels">
                    {['all', ...levels].map(level => (
                        <button
                            key={level}
                            className={`gc-level-chip ${activeFilter === level ? 'active' : ''}`}
                            onClick={() => setLevelFilter(level)}
                        >
                            {level === 'all' ? tx('all') : level}
                        </button>
                    ))}
                </div>
            )}

            {ready && visibleIdeas.map(idea => {
                const isOpen = openId === idea.id;
                const notes = idea.notes.filter(note => (note.pair || 'english-italian') === pairKey);
                return (
                    <div key={idea.id} className={`gc-card ${isOpen ? 'open' : ''}`}>
                        <button className="gc-card-head" onClick={() => setOpenId(isOpen ? null : idea.id)}>
                            <div className="gc-card-head-text">
                                <div className="gc-card-title">{pick(idea.title)}</div>
                                <div className="gc-card-summary">{pick((idea.pairSummary && idea.pairSummary[pairKey]) || idea.summary)}</div>
                            </div>
                            <span className="gc-level">{idea.level}</span>
                            <span className={`gc-chevron ${isOpen ? 'open' : ''}`}>▾</span>
                        </button>

                        {isOpen && (
                            <div className="gc-body">
                                <div className="gc-section-label">{tx('forms')}</div>
                                {[from, to].map(lang => (
                                    <div key={lang} className="gc-forms">
                                        <div className="gc-lang-head">{FLAGS[lang]} {names[lang]}</div>
                                        {(idea.forms[lang] || []).map((form, formIndex) => (
                                            <div key={formIndex} className="gc-form">
                                                <div className="gc-form-name">{labelOf(form.name)}</div>
                                                <div className="gc-form-formula">{pick(form.formula)}</div>
                                            </div>
                                        ))}
                                    </div>
                                ))}

                                <div className="gc-section-label">{tx('examples')}</div>
                                {idea.examples.map((example, i) => (
                                    <div key={i} className="gc-example">
                                        {[from, to].map((lang, idx) => example[lang] && (
                                            <div key={lang} className={`gc-ex-row ${idx === 1 ? 'learn' : ''}`}>
                                                <span className="gc-ex-flag">{FLAGS[lang]}</span>
                                                <div className="gc-ex-main">
                                                    <div className="gc-ex-text">{example[lang].text}</div>
                                                    <div className="gc-ex-tag">{labelOf(example[lang].tag)}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ))}

                                {notes.length > 0 && (
                                    <div className="gc-watch">
                                        <div className="gc-watch-title">💡 {tx('watchOut')}</div>
                                        <ul className="gc-watch-list">
                                            {notes.map((note, i) => (
                                                <li key={i}>{pick(note)}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}